import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Search, Filter, X, ChevronDown, Calendar, User, Building2, ClipboardList } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface PartesFilters {
  search: string;
  estado: string;
  tipoIntervencion: string;
  fechaDesde: string;
  fechaHasta: string;
  tecnicoId: string;
  clienteId: string;
  conOT: string; // 'all' | 'con_ot' | 'sin_ot'
}

interface PartesTrabajoFiltersProps {
  filters: PartesFilters;
  onFiltersChange: (filters: PartesFilters) => void;
  clientes: Array<{ id: string; nombre: string }>;
  tecnicos: Array<{ id: string; nombre: string }>;
  showTecnicoFilter?: boolean;
  resultCount: number;
  estadoCounts: {
    borrador: number;
    finalizado: number;
    facturado: number;
  };
}

export const PartesTrabajoFilters: React.FC<PartesTrabajoFiltersProps> = ({
  filters,
  onFiltersChange,
  clientes,
  tecnicos,
  showTecnicoFilter = false,
  resultCount,
  estadoCounts,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  const updateFilter = (key: keyof PartesFilters, value: string) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const resetFilters = () => {
    onFiltersChange({
      search: '',
      estado: 'all',
      tipoIntervencion: 'all',
      fechaDesde: '',
      fechaHasta: '',
      tecnicoId: 'all',
      clienteId: 'all',
      conOT: 'all',
    });
  };

  const hasActiveFilters = Object.values(filters).some(value => value && value !== 'all');
  const activeFilterCount = Object.entries(filters).filter(([key, value]) => value && value !== 'all').length;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Filter className="h-5 w-5" />
            Filtros y Búsqueda
            {activeFilterCount > 0 && (
              <Badge variant="secondary" className="ml-2">
                {activeFilterCount}
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            <div className="text-sm text-muted-foreground">
              {resultCount} resultado{resultCount !== 1 ? 's' : ''}
            </div>
            <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1">
                  <ChevronDown className={cn("h-4 w-4 transition-transform", isExpanded && "rotate-180")} />
                  {isExpanded ? 'Contraer' : 'Expandir'}
                </Button>
              </CollapsibleTrigger>
            </Collapsible>
          </div>
        </div>
        
        {/* Estado Counts */}
        <div className="flex flex-wrap gap-2">
          <Badge variant="outline" className="bg-yellow-50 border-yellow-200 text-yellow-800">
            Borrador: {estadoCounts.borrador}
          </Badge>
          <Badge variant="outline" className="bg-green-50 border-green-200 text-green-800">
            Finalizado: {estadoCounts.finalizado}
          </Badge>
          <Badge variant="outline" className="bg-blue-50 border-blue-200 text-blue-800">
            Facturado: {estadoCounts.facturado}
          </Badge>
        </div>
      </CardHeader>

      <CardContent>
        {/* Always visible search */}
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por cliente, descripción, número de parte..."
              value={filters.search}
              onChange={(e) => updateFilter('search', e.target.value)}
              className="pl-10"
            />
          </div>

          <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
            <CollapsibleContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Estado */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <ClipboardList className="h-4 w-4" />
                    Estado
                  </Label>
                  <Select value={filters.estado} onValueChange={(value) => updateFilter('estado', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Todos los estados" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los estados</SelectItem>
                      <SelectItem value="borrador">Borrador</SelectItem>
                      <SelectItem value="finalizado">Finalizado</SelectItem>
                      <SelectItem value="facturado">Facturado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Tipo de Intervención */}
                <div className="space-y-2">
                  <Label>Tipo de Intervención</Label>
                  <Select value={filters.tipoIntervencion} onValueChange={(value) => updateFilter('tipoIntervencion', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Todos los tipos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los tipos</SelectItem>
                      <SelectItem value="revision_sistema_seguridad">Revisión seguridad</SelectItem>
                      <SelectItem value="averias">Averías</SelectItem>
                      <SelectItem value="auditoria">Auditoría</SelectItem>
                      <SelectItem value="entrega_materiales">Entrega materiales</SelectItem>
                      <SelectItem value="otros">Otros</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Relación con OT */}
                <div className="space-y-2">
                  <Label>Orden de Trabajo</Label>
                  <Select value={filters.conOT} onValueChange={(value) => updateFilter('conOT', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="con_ot">Con OT asociada</SelectItem>
                      <SelectItem value="sin_ot">Sin OT (independientes)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Fechas */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Fecha desde
                  </Label>
                  <Input
                    type="date"
                    value={filters.fechaDesde}
                    onChange={(e) => updateFilter('fechaDesde', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Fecha hasta
                  </Label>
                  <Input
                    type="date"
                    value={filters.fechaHasta}
                    onChange={(e) => updateFilter('fechaHasta', e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Técnico Filter (solo para admin) */}
                {showTecnicoFilter && (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Técnico
                    </Label>
                    <Select value={filters.tecnicoId} onValueChange={(value) => updateFilter('tecnicoId', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Todos los técnicos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos los técnicos</SelectItem>
                        {tecnicos.map((tecnico) => (
                          <SelectItem key={tecnico.id} value={tecnico.id}>
                            {tecnico.nombre}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Cliente */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Cliente
                  </Label>
                  <Select value={filters.clienteId} onValueChange={(value) => updateFilter('clienteId', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Todos los clientes" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los clientes</SelectItem>
                      {clientes.map((cliente) => (
                        <SelectItem key={cliente.id} value={cliente.id}>
                          {cliente.nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Reset Button */}
          {hasActiveFilters && (
            <div className="pt-2 border-t">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={resetFilters}
                className="gap-2"
              >
                <X className="h-4 w-4" />
                Limpiar filtros
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};